import {
  Appointment,
  Category,
  Event,
  EventSnippet,
  Sponsor,
  Stand,
  UserSnippet,
} from "../data/interfaces";
import firebase from "./../firebase/firebase";
import { STAND_LEVELS } from "../data/enums";
import moment from "moment";
import momentTz from "moment-timezone";

/**
 * Maps a user object to a snippet, used for exchange contacts, business_cards
 * @function
 *
 * @param user
 */
export const mapUserToSnippet = (user: any): UserSnippet => {
  let snippet: UserSnippet = {
    uid: user.uid,
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    profile_image_url: user.profile_image_url || null,
    company: user.company,
    company_role: user.company_role,
    phone_number: user.phone_number || null,
    international_buyer: !!user.international_buyer,
  };

  if (user.mattermost) {
    snippet.mattermost = {
      uid: user.mattermost.uid,
      username: user.mattermost.username,
    };
  }

  return snippet;
};

/**
 * Maps a user object to a snippet, used for track visitors inside stands
 * @function
 *
 * @param user
 */
export const mapUserToVisitorSnippet = (user: any): UserSnippet => {
  return {
    uid: user.uid,
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    profile_image_url: user.profile_image_url || null,
    company: user.company,
    company_role: user.company_role,
  };
};

/**
 * Mapping the stand into a standard object for use within the project
 * @function
 *
 * @param id
 * @param response
 */
export const mapEvent = (id: string, response: any): Event => {
  const event: Event = {
    id: id,
    name: response.name,
    description: response.description,
    start_datetime: response.start_datetime,
    end_datetime: response.end_datetime,
    image_url: response.image_url,
    // categories: response.categories ? response.categories : [],
    relators: response.relators ? response.relators : [],
    recording_url: response.recording_url,
    stand: response.stand,
    zoom: response.zoom,
    jitsi: response.jitsi,
    congress_room: response.congress_room,
    tags: response.tags,
  };
  return event;
};

/**
 * Map event obj to a snippet useful to replicate data
 * @function
 *
 * @param event
 */
export const mapEventToSnippet = (event: Event): EventSnippet => {
  return {
    id: event.id,
    name: event.name,
    image_url: event.image_url,
    start_datetime: event.start_datetime.seconds
      ? firebase.firestore.Timestamp.fromMillis(event.start_datetime.seconds * 1000)
      : event.start_datetime,
    end_datetime: event.end_datetime.seconds
      ? firebase.firestore.Timestamp.fromMillis(event.end_datetime.seconds * 1000)
      : event.end_datetime,
    stand: event.stand || null,
  };
};

/**
 * Mapping the stand into a standard object for use within the project
 * @function
 *
 * @param id
 * @param response
 */
export function mapStand(id: string, response: any): Stand {
  const stand: Stand = {
    id: id,
    name: response.name,
    description: response.description,
    categories: response.categories ? response.categories : [],
    color: response.color || null,
    logo_image_url: response.logo_image_url,
    cover_image_url: response.cover_image_url,
    tags: response.tags,
    email: response.email,
    level: response.level,
    owner_name: response.owner_name,
    products: response.products,
    website: response.website,
    brochures: response.brochures,
    media: response.media,
    visitors_count: response.visitors_count || 0,
  };

  if (response.mattermost) {
    stand.mattermost = response.mattermost;
  }

  return stand;
}

export function mapSponsor(id: string, data: any): Sponsor {
  return {
    id: id,
    name: data.name,
    image_url: data.image_url,
  };
}

/**
 * Maps a value from database to its select option (used by forms)
 * @function
 *
 * @param value
 * @param options
 */
export const mapFieldToSelectValue = (value: any, options: { label: string; value: any }[]) => {
  if (!value) {
    return null;
  }

  let results = options.filter((p) => p.value === value);

  if (!!results && !!results.length) {
    return results[0];
  }

  // Not found
  return null;
};

/**
 * Maps a select value from a form to its value (used by db)
 * @function
 *
 * @param option
 */
export const mapSelectValueToField = (option: any) => {
  if (!option) {
    return null;
  }

  return option.value;
};

/**
 * Maps user company data from formik and prepare to db
 * @function
 *
 * @param data
 */
export const mapUserCompanyData = (data: any) => {
  let map: any = {
    company: {
      name: data.company_name,
      user_role: data.company_user_role,
      type: mapSelectValueToField(data.company_type),
      core_business: data.company_core_business,
      email: data.company_email || null,
      segment: mapSelectValueToField(data.company_segment),
      phone_number: data.company_phone_number || null,
      address: {
        street: data.company_address_street,
        zip_code: data.company_address_zip_code,
        city: data.company_address_city,
        state: data.company_address_state,
        country: mapSelectValueToField(data.company_address_country),
      },
    },
  };

  return map;
};

/**
 * Maps user personal data from formik and prepare to db
 * @function
 *
 * @param data
 */
export const mapUserPersonalData = (data: any) => {
  let map: any = {
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone_number: data.phone_number,
    prev_edition: !!data.prev_edition,
    international_buyer: !!data.international_buyer,
    privacy_policy_consent: !!data.privacy_policy_consent,
  };

  return map;
};

/**
 * Maps user cfu data from formik and prepare to db
 * @function
 *
 * @param data
 */
export const mapUserCfuData = (data: any) => {
  let map: any = {
    cfu_info: {
      membership_group: mapSelectValueToField(data.cfu_info_membership_group),
      membership_group_location: data.cfu_info_membership_group_location,
      subscription_number: data.cfu_info_subscription_number,
      fiscal_code: data.cfu_info_fiscal_code,
    },
  };

  return map;
};

/**
 * Maps category object from firebase
 * @function
 *
 * @param id
 * @param response
 */
export const mapCategory = (id: string, response: any): Category => {
  return {
    id: id,
    name: response.name,
    color: response.color,
    text_color: response.text_color || null,
    keywords: response.keywords || [],
  };
};

/**
 * Returns stand level name
 * @function
 *
 * @param level
 */
export const getStandLevelName = (level: STAND_LEVELS): string => {
  switch (level) {
    case STAND_LEVELS.SPONSOR: {
      return "sponsor";
    }
    case STAND_LEVELS.BASIC: {
      return "basic";
    }
    case STAND_LEVELS.ADVANCED: {
      return "advanced";
    }
    case STAND_LEVELS.ADVANCED_PLUS: {
      return "advanced-plus";
    }
    default: {
      return "";
    }
  }
};

/**
 * Maps appointment object.
 * @function
 *
 * @param id
 * @param data
 */
export const mapAppointment = (id: string, data: any): Appointment => {
  const appointment: Appointment = {
    id: id,
    accepted: data.accepted,
    created_by: data.created_by,
    start_datetime: data.start_datetime,
    end_datetime: data.end_datetime,
    notes: data.notes,
    jitsi_url: data.jitsi_url,
    stand: data.stand,
    visitor: data.visitor,
    members: data.members,
  };

  return appointment;
};

/**
 * Maps appointment object.
 * @function
 *
 * @param momentObj
 * @param reset
 */
export const mapMomentObjToRomeZoneDate = (momentObj: moment.Moment, reset: boolean = false): Date => {
  const dateFormat = "DD-MM-YYYY h:mm:ss";


  var m = momentTz.utc(momentObj.format(dateFormat), dateFormat); // parse input as UTC
  let res = m.clone().tz("Europe/Rome");

  if (reset) {
    res = res.clone().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
  }

  return res.toDate();
};

// TODO: COMMENT
export const momentInRome = (date?: any, reset: boolean = false): moment.Moment => {
  const timeZone: string = "Europe/Rome";
  let res = date ? momentTz.tz(date, timeZone) : momentTz.tz(timeZone);

  if (reset) {
    res = res.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  }
  return res;
};

/**
 * Replace all placeholder in a string
 * @function
 *
 * @param str
 * @param mapObj
 */
export const replacePlaceholders = (str: string, mapObj: any): string => {
  const re = new RegExp(Object.keys(mapObj).join("|"), "gi");

  return str.replace(re, function(matched) {
    return mapObj[matched];
  });
};
